import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { ApiResponse, Pagination } from '../../../models/base';
import { inject } from '@angular/core';
import { Logger } from '../logger/logger';

// npm install @tanstack/table-core

/**
 * Generic base service to centralize common CRUD HTTP operations.
 *
 * This class is designed to be extended by specific data services
 * (e.g., `UserService`, `RoleService`, `CategoryService`).
 *
 * Key features:
 * - Does **not** handle authentication tokens (handled by an HTTP interceptor).
 * - Uses generics for strong typing of entities (T) and DTOs (D).
 *
 * @template T The type of the main entity (e.g., `Role`, `Category`).
 * @template C The type of the data transfer object used for create/update operations.
 * @template U The type of the data transfer object used for update operations.
 */
export abstract class BaseApiService<T, C = T, U = C> {
  /** Base URL for all API endpoints (from environment configuration). */
  protected baseUrl = environment.apiUrl;

  private logger = inject(Logger);

  /**
   * The base resource path for this entity, e.g. `"roles"`, `"categories"`.
   * Must be set by the subclass constructor.
   */
  protected resourcePath = '';

  /**
   * Creates an instance of the base API service.
   * @param http Angular HttpClient used to perform HTTP requests.
   */
  constructor(protected http: HttpClient) {}

  /**
   * Fetches all entities from the API.
   *
   * @returns An Observable containing an `ApiResponse` with an array of entities.
   */
  getAll(): Observable<ApiResponse<T[]>> {
    return this.http
      .get<ApiResponse<T[]>>(`${this.baseUrl}/${this.resourcePath}`)
      .pipe(tap((res) => this.logger.debug(`GET ALL ${this.resourcePath}:`, res)));
  }

  /**
   * Fetches a paginated list of entities from the API.
   *
   * @param params Optional query parameters used to customize the request.
   * Supported parameters include:
   * - `page`: Page number (e.g., `2`)
   * - `limit`: Number of items per page (e.g., `10`)
   * - `search`: Text query for filtering results
   * - `sort`: Sorting object (e.g., `{ name: 'asc', date: 'desc' }`)
   * - `dateFrom`: Start date for filtering (e.g., `2024-01-01`)
   * - `dateTo`: End date for filtering (e.g., `2024-12-31`)
   * - Other filter objects or arrays for advanced filtering
   *
   * @returns An observable emitting the paginated API response.
   *
   * @example
   * GET https://api.monsite.com/products/paginate?page=2&limit=10&search=phone&category=electronics&sort=name:asc,date:desc&dateFrom=2024-01-01&dateTo=2024-12-31
   */
  getPaginated(params?: {
    [key: string]: any;
  }): Observable<ApiResponse<{ list: T[]; pagination: Pagination }>> {
    let httpParams = new HttpParams();

    if (params) {
      Object.keys(params).forEach((key) => {
        const value = params[key];

        if (value === null || value === undefined) return;

        //  Case 1: If the value is an object (e.g., { name: 'asc', date: 'desc' })
        if (typeof value === 'object' && !Array.isArray(value)) {
          if (key === 'sort') {
            // Convert sort object into query string format: ?sort=name:asc,date:desc
            const sortString = Object.entries(value)
              .map(([field, direction]) => `${field}:${direction}`)
              .join(',');
          if(sortString)  httpParams =  httpParams.set('sort', sortString);
          } else {
            // Handle other nested object filters (e.g., filters by attributes)
            Object.entries(value).forEach(([subKey, subValue]) => {
              if (subValue !== null && subValue !== undefined) {
                httpParams = httpParams.set(`${key}.${subKey}`, String(subValue));
              }
            });
          }
        }

        // Case 2: If the value is an array → ?key=value1&key=value2
        else if (Array.isArray(value)) {
          value.forEach((v) => {
            httpParams = httpParams.append(key, v);
          });
        }

        // Case 3: Simple value (string, number, boolean, date)
        else {
          httpParams = httpParams.set(key, value);
        }
      });
    }

    return this.http
      .get<any>(`${this.baseUrl}/${this.resourcePath}/paginate`, {
        params: httpParams,
      })
      .pipe(tap((res) => this.logger.debug(`GET PAGINATED ${this.resourcePath}:`, res)));
  }

  /**
   * Creates a new entity on the server.
   *
   * @param data The DTO containing the data for the new entity.
   * @returns An Observable containing an `ApiResponse` with the created entity.
   */
  create(data: C): Observable<ApiResponse<T>> {
    return this.http
      .post<ApiResponse<T>>(`${this.baseUrl}/${this.resourcePath}`, data)
      .pipe(tap((res) => this.logger.debug(`CREATE ${this.resourcePath}:`, res)));
  }

  /**
   * Updates an existing entity on the server.
   *
   * @param id The unique identifier of the entity to update.
   * @param data The DTO containing the updated entity data.
   * @returns An Observable containing an `ApiResponse` with the updated entity.
   */
  update(id: number, data: U): Observable<ApiResponse<T>> {
    return this.http
      .put<ApiResponse<T>>(`${this.baseUrl}/${this.resourcePath}/${id}`, data)
      .pipe(tap((res) => this.logger.debug(`UPDATE ${this.resourcePath} [${id}]:`, res)));
  }

  /**
   * Deletes an entity by its identifier.
   *
   * @param id The unique identifier of the entity to delete.
   * @returns An Observable containing an `ApiResponse` with the deleted entity
   * (or `null` if the entity has been permanently removed).
   */
  delete(id: number): Observable<ApiResponse<T | null>> {
    return this.http
      .delete<ApiResponse<T | null>>(`${this.baseUrl}/${this.resourcePath}/${id}`)
      .pipe(tap((res) => this.logger.debug(`DELETE ${this.resourcePath} [${id}]:`, res)));
  }

  /**
   * Changes the status of an entity.
   *
   * @param id The unique identifier of the entity.
   * @param data An object containing the new status, e.g., `{ isActive: true }`.
   * @returns An Observable containing an `ApiResponse` with the updated entity.
   */
  changeStatus(id: number, data: { isActive: boolean }): Observable<ApiResponse<T>> {
    return this.http
      .patch<ApiResponse<T>>(`${this.baseUrl}/${this.resourcePath}/${id}/status`, data)
      .pipe(
        tap((res) => this.logger.debug(`CHANGE STATUS ${this.resourcePath} [${id}] ->`, data, res))
      );
  }
}
